<div class="container legal-policy-page">
  <div class="row mx-2">
    <h1 class="col-12 legal-policy-page__title">Privacy Policy</h1>
    <p class="col-12 legal-policy-page__meta">
      <strong>Effective Date: October 30, 2026</strong>
    </p>
    <p class="col-12 my-3 text-justify">
      This Privacy Policy describes what personal information we collect through
      our web and mobile apps ("Platform"), how we collect the information, and
      for what purpose. Jikia, doing business as CitizeX, is the controller for
      this information. We process account, RSVP, event-access, and attendance
      information to provide the service you request; we do not rely on a
      blanket "use means consent" statement.
    </p>
  </div>
  <hr />
  <div class="row mx-2 terms-list-style list">
    <div class="col-12">
      <h2 class="legal-policy-page__section-title">
        1. Information we collect
      </h2>
      <ul style="list-style-type: disc !important; padding-left: 1.5rem">
        <li class="my-2">
          <strong>Information Provided By You:</strong> We collect and store
          personal information you provide when you fill out a form, register or
          create an account on our Platform, including your name, email address,
          <strong>phone number</strong>, and any other personal information you
          choose to share.
        </li>
        <li class="my-2">
          <strong>Information Collected Automatically:</strong> When you access
          our Platform, we may automatically collect and store information about
          your device such as your IP address, device type, geographic location
          data, and unique device identifier.
        </li>
        <li class="my-2">
          <strong>Cookies and Tracking:</strong> We use cookies to recognize
          you, understand your preferences, and improve your user experience.
          You can set your browser to refuse cookies, though some features of
          the Platform may not function properly as a result.
        </li>
      </ul>
      <hr />
      <h2 class="legal-policy-page__section-title">
        2. How we use the information we collect
      </h2>
      <p class="mt-2 text-justify">We use the information to:</p>
      <ul style="list-style-type: disc !important; padding-left: 1.5rem">
        <li class="my-2">Communicate and respond to your inquiries.</li>
        <li class="my-2">Verify your identity via SMS/Text verification.</li>
        <li class="my-2">
          Improve user experience and maintain internal records.
        </li>
        <li class="my-2">
          Send necessary account, authentication, RSVP, cancellation, reminder,
          and event-access messages. These operational messages are not
          promotional.
        </li>
      </ul>
      <hr />
      <h2 class="legal-policy-page__section-title">
        3. Third parties with whom we share your information
      </h2>
      <p class="mt-2 text-justify">
        We may share information with the following parties:
      </p>
      <ul style="list-style-type: disc !important; padding-left: 1.5rem">
        <li class="my-2">
          <strong>Service Providers:</strong> Third-party partners who help
          operate our Platform (e.g., hosting, email services, and analytics).
        </li>
        <li class="my-2">
          <strong>Event Hosts:</strong> We share email and an available phone
          number with event hosts only when you turn on the optional
          account-level Event host contact sharing setting. It is off by
          default, can be changed in Account settings, and applies only to
          event-related follow-up. Withdrawal stops future CitizeX disclosures
          and does not cancel an RSVP.
        </li>
        <li class="my-2">
          <strong>TODA webinar provider:</strong> When you select Join, CitizeX
          sends your selected public display label and an event-scoped,
          non-routable pseudonymous attendee alias to TODA to provide the live
          session and reconcile attendance. TODA does not receive your CitizeX
          account email, phone number, legal/private name, or account ID from
          this handoff.
        </li>
        <li class="my-2">
          <strong>Legal Requirements:</strong> As required by law or to protect
          the safety and rights of CitizeX and its users.
        </li>
      </ul>
      <hr />
      <h2 class="legal-policy-page__section-title">
        4. SMS and mobile messaging (10DLC compliance)
      </h2>
      <p class="mt-2 text-justify">
        CitizeX values your privacy regarding mobile communication.
        <strong>Please note the following:</strong>
      </p>
      <ul style="list-style-type: disc !important; padding-left: 1.5rem">
        <li class="my-2">
          <strong>No Sharing of SMS Data:</strong> No mobile information will be
          shared with third parties or affiliates for marketing or promotional
          purposes. All the above categories of sharing exclude text messaging
          originator opt-in data and consent; this information will not be
          shared with any third parties.
        </li>
        <li class="my-2">
          <strong>Opt-In:</strong> By providing your phone number and checking
          the consent box, you agree to receive one-time verification codes.
          Consent is not a condition of purchase or a requirement to use the
          platform.
        </li>
        <li class="my-2">
          <strong>Opt-Out:</strong> You may opt out of SMS communications at any
          time by replying <strong>STOP</strong> to any message received. For
          support, reply <strong>HELP</strong>.
        </li>
        <li class="my-2">
          <strong>Rates:</strong> Message and data rates may apply. Message
          frequency varies.
        </li>
      </ul>
      <hr />
      <h2 class="legal-policy-page__section-title">
        5. Right to opt in and opt out
      </h2>
      <p class="mt-2 text-justify">
        You may withdraw optional host-contact sharing in Account settings. You
        may request access, correction, deletion, portability, restriction, or
        objection where applicable by contacting
        <strong>info&#64;citizex.ai</strong>. You may also complain to your
        local data-protection authority where that right applies.
      </p>
      <hr />
      <h2 class="legal-policy-page__section-title">
        6. Retention, deletion, and transfers
      </h2>
      <p class="mt-2 text-justify">
        We keep account/contact data while an account is active. After an
        accepted deletion request, we delete or de-identify live contact, RSVP,
        and related identifiers within 24 hours; rolling backups expire within
        35 days. For events created under this updated policy, identifiable
        RSVP, attendance, and the CitizeX-held TODA alias mapping are kept for
        two years after an event ends to support event administration, host
        reporting, and event-related follow-up, then deleted or de-identified;
        aggregate-only totals may remain. Events created under the previous
        policy keep their original 30-day period after the event ends. Active
        host-contact evidence is retained while active and for three years after
        withdrawal, last disclosure, or account deletion. Provider-session
        retention is controlled by TODA's configured service retention. Where
        information is transferred outside your jurisdiction, we use applicable
        safeguards.
      </p>
      <hr />
      <h2 class="legal-policy-page__section-title">7. Data security</h2>
      <p class="mt-2 text-justify">
        We implement reasonable technical and organizational measures to protect
        your personal information. However, no security system is perfect. Do
        not send sensitive information (such as passwords or SSNs) via insecure
        email or "contact us" forms.
      </p>
      <hr />
      <h2 class="legal-policy-page__section-title">8. Children</h2>
      <p class="mt-2 text-justify">
        Our Platform is not intended for or directed at children under the age
        of 16. We do not knowingly collect personal information from children
        under 16.
      </p>
      <hr />
      <h2 class="legal-policy-page__section-title">9. Contact us</h2>
      <p class="mt-2 text-justify">
        If you have any questions regarding this Privacy Policy, please contact
        us at: <strong>Email:</strong> info&#64;citizex.ai
      </p>
      <hr />
      <h2 class="legal-policy-page__section-title">
        10. Privacy policy updates
      </h2>
      <p class="mt-2 text-justify mb-5">
        We may update this Privacy Policy to reflect changes to our practices.
        If we make material changes, we will post the amended version on the
        Platform.
      </p>
    </div>
  </div>
</div>
